// app/src/screens/Script.jsx — the script + render gate (handoff §4), pixel-close.
// P2-SPLIT: two authoring halves behind the hard approve gate.
//   A) WRITE — draftScript({setJobId,cardId}) streams a readable SCENE-CARD script (what you SEE /
//      what's SAID / how it FEELS) live over the job's step feed; a 30-credit pass. The steer box
//      re-writes (another 30-credit pass). NO credits for render are spent yet.
//   B) APPROVE — the user reads + approves the script; draftCompile enqueues the COMPILE job (free)
//      that turns the approved script into validated gen-blocks (a "building…" beat). On success we
//      draftApprove({quoteOnly}) for the REAL credit quote → draftApprove({confirm}) → Generating.
// Never passes `mode` (the server forces live; the dev rig alone honours mock). A 409 with a resume
// pointer means the draft already has its render — we follow the pointer.

import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, TriangleAlert } from 'lucide-react';
import { navigate } from '@common/router.js';
import { watchJob } from '@common/progress.js';
import { getDraft, draftScript, draftCompile, draftApprove } from '@common/drafts.js';
import { newIdemKey } from '@common/commands.js';
import { useI18n } from '../i18n/index.jsx';
import { useSession } from '../lib/session.jsx';
import { useFlow } from '../lib/flow.jsx';
import { useIsMobile } from '../lib/useIsMobile.js';
import { useToast } from '../lib/toast.jsx';
import { useTopUp } from '../lib/topup.jsx';
import { StepIndicator } from '../components/ui.jsx';
import { resumeRoute } from '../lib/create-nav.js';
import { fmtLength, langName, effectiveSettings } from '../lib/settings.js';

const LIVE = ['queued', 'running'];
const SCENE_ACCENTS = ['coral', 'violet', 'mint', 'tangerine'];

export default function Script() {
  const { t } = useI18n();
  const { api, refreshCredits } = useSession();
  const { draftId, pick, clearPick } = useFlow();
  const mobile = useIsMobile();
  const toast = useToast();
  const openTopUp = useTopUp();
  const [draft, setDraft] = useState(null);
  const [watching, setWatching] = useState(false);   // WRITE pass in flight
  const [compiling, setCompiling] = useState(false);  // COMPILE job in flight (the approve beat)
  const [streamScenes, setStreamScenes] = useState([]); // scene cards arriving live as `scene` step-events
  const [scriptGate, setScriptGate] = useState(null); // { quote, pick?, steer? } | { ooc, credits_required, credits_balance }
  const [renderQuote, setRenderQuote] = useState(null);
  const [renderGate, setRenderGate] = useState(null); // { kind:'insufficient_credits'|'refused', ... }
  const [steerText, setSteerText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const scriptKey = useRef(null);
  const compileKey = useRef(null);
  const approveKey = useRef(null);
  const stopRef = useRef(null);

  const pickFrom = (d) => pick || (d && d.chosen_idea ? { setJobId: d.chosen_idea.job_id, cardId: d.chosen_idea.card_id } : null);
  // CATCHUP F1: a transient pick naming a DIFFERENT card than chosen_idea = the user came back from
  // "← change idea" and picked another. Re-write (its own 30-credit gate) — never render the new
  // title over the old scenes, never compile/approve the old card.
  const repickedFrom = (d) => !!(pick && d && d.chosen_idea
    && (String(pick.setJobId) !== String(d.chosen_idea.job_id) || String(pick.cardId) !== String(d.chosen_idea.card_id)));

  const refresh = async () => {
    const r = await getDraft(api, draftId);
    if (r.kind === 'ok') { setDraft(r.body); return r.body; }
    setErr(r.error || t('common.error'));
    return null;
  };

  // WRITE: the streamed scene-card script (mirrors the Ideas card stream). rtdb:true wires the live
  // step feed; each `scene` step-event is one completed card. On terminal → refresh + show Approve.
  function watchScript(jobId) {
    if (stopRef.current) stopRef.current();
    setWatching(true); setStreamScenes([]); setRenderQuote(null);
    stopRef.current = watchJob({
      api, jobId, rtdb: true,
      onStep: (s) => {
        if (s && s.step === 'scene' && s.index != null) {
          setStreamScenes((prev) => {
            const next = prev.filter((c) => c.index !== s.index);
            next.push({ index: s.index, slug: s.slug || '', seconds: s.seconds });
            return next.sort((a, b) => a.index - b.index);
          });
        }
      },
      onState: async ({ state }) => {
        if (['succeeded', 'failed', 'refused', 'cancelled'].includes(state)) {
          setWatching(false); setStreamScenes([]);
          const d = await refresh(); refreshCredits();
          // the pick is now durably recorded (chosen_idea) — drop the transient one (F1). The script
          // is READ-only now; the user reads + approves (no auto-quote — approve triggers compile).
          if (d && d.script && d.script.state === 'succeeded') clearPick();
        }
      },
    });
  }

  // COMPILE: the approve beat. draftCompile enqueued the job; watch it to done, then quote.
  function watchCompile(jobId) {
    if (stopRef.current) stopRef.current();
    setCompiling(true);
    stopRef.current = watchJob({
      api, jobId, rtdb: true,
      onState: async ({ state }) => {
        if (['succeeded', 'failed', 'refused', 'cancelled'].includes(state)) {
          setCompiling(false);
          const d = await refresh(); refreshCredits();
          if (d && d.compile && d.compile.state === 'succeeded') quoteRender();
          else if (d && d.compile && d.compile.error) setErr(d.compile.error);
        }
      },
    });
  }

  useEffect(() => {
    if (!draftId) { navigate('/'); return; }
    (async () => {
      const d = await refresh();
      if (!d) return;
      const sc = d.script;
      const cmp = d.compile;
      if (sc && LIVE.includes(sc.state)) { watchScript(sc.job_id); return; }
      // F1: a re-pick re-writes through its own honest 30-credit gate before anything else.
      if (repickedFrom(d)) { runScript({ pick }); return; }
      if (cmp && LIVE.includes(cmp.state)) { watchCompile(cmp.job_id); return; }
      // only a FRESH compile (off the current script) is quotable; a stale one (re-write since) needs
      // a recompile — fall through to show the scenes + Approve, which enqueues a fresh compile.
      if (cmp && cmp.state === 'succeeded' && !cmp.stale) { quoteRender(); return; }
      if (sc && sc.state === 'succeeded') return; // script ready → read + Approve (compile happens on approve)
      // phase A — no finished script yet. Resolve the chosen card, then WRITE (a 30-credit gate).
      const p = pickFrom(d);
      if (!p) { navigate('/create/ideas'); return; }
      runScript({ pick: p });
    })();
    return () => { if (stopRef.current) stopRef.current(); };
  }, [draftId]); // eslint-disable-line react-hooks/exhaustive-deps

  // WRITE: one script pass (generate or steer). confirm=false prices (needs_confirm, no spend);
  // confirm=true re-sends the SAME setJobId+cardId (+steer) through the gate and enqueues.
  async function runScript({ pick: p = null, steer = null, confirm = false } = {}) {
    if (busy || !draftId) return;
    setBusy(true); setErr(null);
    if (!confirm) scriptKey.current = newIdemKey();
    const body = { steer: steer || null, confirm, idempotencyKey: scriptKey.current };
    if (p) { body.setJobId = p.setJobId; body.cardId = p.cardId; }
    const r = await draftScript(api, draftId, body);
    setBusy(false);
    if (r.kind === 'ok') {
      setScriptGate(null); setSteerText('');
      // a steer supersedes any compiled/quoted state — drop the render quote/gate (they were off the
      // OLD script) and reset the compile key so Approve enqueues a FRESH compile (the server-side
      // staleness fence is the real guard; this removes the reachable stale-approve UI path).
      setWatching(true); setRenderQuote(null); setRenderGate(null); setCompiling(false); compileKey.current = null;
      const d = await refresh(); refreshCredits();
      if (d && d.script && LIVE.includes(d.script.state)) watchScript(d.script.job_id);
      else { setWatching(false); if (d && d.script && d.script.state === 'succeeded') clearPick(); }
    } else if (r.kind === 'needs_confirm') {
      setScriptGate({ quote: r.quote, pick: p, steer });
    } else if (r.kind === 'insufficient_credits') {
      setScriptGate({ ooc: true, credits_required: r.credits_required, credits_balance: r.credits_balance });
    } else {
      setErr(r.error || t('common.error'));
    }
  }

  // APPROVE (step 1): the hard gate. The user has read the script — turn it into gen-blocks (free
  // COMPILE job), then the render quote appears.
  async function runCompile() {
    if (busy || compiling) return;
    setBusy(true); setErr(null); setRenderGate(null);
    if (!compileKey.current) compileKey.current = newIdemKey();
    const r = await draftCompile(api, draftId, { idempotencyKey: compileKey.current });
    setBusy(false);
    if (r.kind === 'ok') {
      setCompiling(true);
      const d = await refresh();
      if (d && d.compile && LIVE.includes(d.compile.state)) watchCompile(d.compile.job_id);
      else if (d && d.compile && d.compile.state === 'succeeded') { setCompiling(false); quoteRender(); }
      else setCompiling(false);
    } else if (r.kind === 'error' && r.body && r.body.resume) navigate(resumeRoute(r.body.resume));
    else setErr(r.error || t('common.error'));
  }

  // APPROVE (step 2): the render quote (never a row) off the compiled blocks.
  async function quoteRender() {
    const r = await draftApprove(api, draftId, { quoteOnly: true });
    if (r.kind === 'ok') { setRenderQuote(r.body.quote); setRenderGate(null); }
    else if (r.kind === 'insufficient_credits' || r.kind === 'refused') setRenderGate(r);
    else if (r.kind === 'error' && r.body && r.body.resume) navigate(resumeRoute(r.body.resume));
    else setErr(r.error || t('common.error'));
  }

  // APPROVE (step 3): confirm the render — real credits → Generating.
  async function confirmRender() {
    if (busy) return; setBusy(true); setErr(null);
    if (!approveKey.current) approveKey.current = newIdemKey(); // minted ONCE; retries reuse it
    const r = await draftApprove(api, draftId, { confirm: true, idempotencyKey: approveKey.current });
    setBusy(false);
    if (r.kind === 'ok') { refreshCredits(); navigate(`/create/generating/${encodeURIComponent(r.body.job_id)}`); }
    else if (r.kind === 'needs_confirm') setRenderQuote(r.quote);
    else if (r.kind === 'insufficient_credits') setRenderGate(r);
    else if (r.kind === 'error' && r.body && r.body.resume) navigate(resumeRoute(r.body.resume)); // already has its render
    else setErr(r.error || t('common.error'));
  }

  if (err && !draft) return <div className="view"><p className="muted" style={{ color: 'var(--coral)' }}>{err}</p></div>;
  if (!draft) return <div className="view"><p className="muted">{t('common.loading')}</p></div>;

  const sc = draft.script;
  const scenes = (sc && sc.scenes) || [];
  const repicking = repickedFrom(draft);
  const hasScript = !repicking && !!(sc && sc.state === 'succeeded' && scenes.length);
  // during the WRITE stream, show the partial cards coming in; when done, the full scene cards.
  const displayScenes = hasScript ? scenes : streamScenes.map((s) => ({ id: `${s.index}`, slug: s.slug, seconds: s.seconds }));
  // SETTINGS-THREAD: length + aspect are LOCKED at the Script stage (display-only chips). Show the
  // SETTLED values — the script's real length (a content steer may have resized it) + the pinned
  // aspect/language. Warnings (LENGTH_CHANGED / _INVALID / *_CHANGED) are engine DATA → chips.
  const scriptSettings = (sc && sc.settings) || (draft.compile && draft.compile.settings) || null;
  const eff = effectiveSettings(draft.settings, scriptSettings);
  const aspect = eff.aspect;
  const pieceLen = (sc && sc.script && sc.script.length_s) || eff.length_s;
  const warnings = (sc && sc.warnings) || [];
  const chosenCard = (() => {
    const p = pickFrom(draft);
    if (p) { const set = (draft.idea_sets || []).find((s) => s.job_id === p.setJobId); const c = set && set.set && (set.set.cards || []).find((x) => x.id === p.cardId); if (c) return c; }
    return (draft.chosen_idea && draft.chosen_idea.snapshot) || null;
  })();
  const title = (chosenCard && chosenCard.hook) || (sc && sc.script && sc.script.title) || t('script.title');
  const renderFailed = draft.approve_job && draft.approve_job.state === 'failed';

  const statusChips = [
    { c: 'var(--coral)', v: fmtLength(pieceLen) },
    { c: 'var(--violet)', v: aspect },
    { c: 'var(--mint)', v: langName(eff.language) },
  ];
  // a warning → a human line. LENGTH shows the new duration; ASPECT/LANGUAGE show the new value.
  const warnText = (w) => {
    const val = w.code.startsWith('LENGTH') ? fmtLength(w.new_value) : w.code.startsWith('LANGUAGE') ? langName(w.new_value) : w.new_value;
    return t(`settings.warn.${w.code}`, { value: val });
  };
  const warnBad = (code) => /INVALID$/.test(code);

  const creditFace = renderQuote && renderQuote.credits;
  const credited = creditFace && creditFace.credits > 0;
  const seconds = renderQuote ? Math.round(renderQuote.footage_seconds || renderQuote.container_seconds || 0) : null;
  const ooc = renderGate && renderGate.kind === 'insufficient_credits';

  return (
    <div className="view" style={{ maxInlineSize: 780 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <StepIndicator current="script" size={mobile ? 'sm' : 'lg'} />
        {!mobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            {statusChips.map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999, background: 'var(--surface)', border: '1px solid var(--border)', font: '600 12px var(--font-body)' }}>
                <span style={{ width: 7, height: 7, borderRadius: 999, background: s.c }} />{s.v}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '24px 0 4px', flexWrap: 'wrap' }}>
        <h2 className="h-section" style={{ margin: 0 }}>{title}</h2>
        <button className="q-btn" onClick={() => navigate('/create/ideas')} style={{ font: '600 11.5px var(--font-mono)', color: 'var(--violet)', background: 'var(--violet-tint)', borderRadius: 999, padding: '5px 11px', border: 'none' }}>← {t('script.changeIdea')}</button>
      </div>
      <p className="muted" style={{ margin: '0 0 20px', fontSize: mobile ? 12.5 : 14, lineHeight: 1.5 }}>{t('script.subcopy')}</p>

      {renderFailed && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--coral-tint)', borderRadius: 12, padding: '11px 14px', marginBottom: 16 }}>
          <TriangleAlert size={16} color="var(--coral)" strokeWidth={2.2} />
          <span style={{ font: '500 12.5px var(--font-body)', color: 'var(--coral-ink, var(--coral))' }}>{t('script.renderFailedNote')}</span>
        </div>
      )}

      {/* SETTINGS-THREAD: warning chips driven by the engine's warnings[] DATA (a steer that
          resized the piece, or a change that couldn't apply). */}
      {warnings.map((w, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, background: warnBad(w.code) ? 'var(--coral-tint)' : 'var(--violet-tint)', borderRadius: 11, padding: '9px 13px', marginBottom: 10 }}>
          <TriangleAlert size={15} color={warnBad(w.code) ? 'var(--coral)' : 'var(--violet)'} strokeWidth={2.2} />
          <span style={{ font: '500 12.5px var(--font-body)', color: 'var(--text)' }}>{warnText(w)}</span>
        </div>
      ))}

      {/* PHASE A — writing the script (scene cards stream in live) */}
      {watching && (
        <>
          {displayScenes.length > 0 && <SceneList scenes={displayScenes} mobile={mobile} t={t} partial />}
          <div style={{ textAlign: 'center', padding: displayScenes.length ? '20px 0 8px' : '48px 0' }}>
            <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 999, background: 'var(--coral)', animation: 'pulseDot 1.3s ease-in-out infinite' }} />
            <p className="muted" style={{ marginTop: 14, font: '600 14px var(--font-body)' }}>{t('script.writing')}</p>
          </div>
        </>
      )}

      {!watching && !hasScript && scriptGate && (
        <div className="q-card" style={{ maxWidth: 520, margin: '10px auto', padding: 20, borderRadius: 16, border: '1px solid var(--coral-line)', textAlign: 'center' }}>
          {scriptGate.ooc ? (
            <>
              <h4 className="h-card" style={{ margin: '0 0 6px' }}>{t('ideas.oocTitle')}</h4>
              <p className="muted" style={{ fontSize: 13, margin: '0 0 14px' }}>{t('script.needsBalance', { required: scriptGate.credits_required, balance: scriptGate.credits_balance })}</p>
              <button className="q-btn press" onClick={openTopUp} style={{ background: 'var(--coral)', color: '#fff', padding: '12px 22px', borderRadius: 12, font: '700 13.5px var(--font-body)' }}>{t('script.outOfCredits')}</button>
            </>
          ) : (
            <>
              <h4 className="h-card" style={{ margin: '0 0 6px' }}>{t('script.generateTitle')}</h4>
              <p className="muted" style={{ fontSize: 13, margin: '0 0 14px' }}>{t('script.generatePrice', { credits: (scriptGate.quote && scriptGate.quote.credits && scriptGate.quote.credits.credits) || '—' })}</p>
              <button className="q-btn press" disabled={busy} onClick={() => runScript({ pick: scriptGate.pick, steer: scriptGate.steer, confirm: true })} style={{ background: 'var(--coral)', color: '#fff', padding: '13px 26px', borderRadius: 12, font: '700 14px var(--font-body)', boxShadow: '0 6px 16px rgba(255,81,71,.28)', opacity: busy ? 0.6 : 1 }}>{t('script.generateCta')} →</button>
            </>
          )}
        </div>
      )}

      {/* PHASE B — read the script + steer + approve */}
      {hasScript && !watching && (
        <>
          <SceneList scenes={displayScenes} mobile={mobile} t={t} />

          {/* steer input — another 30-credit WRITE pass (locked once compiling/quoted/out-of-credits) */}
          {!compiling && !renderQuote && !renderGate && (
            <form onSubmit={(e) => { e.preventDefault(); if (steerText.trim()) runScript({ steer: steerText.trim() }); }} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: mobile ? 14 : 16, padding: mobile ? '9px 9px 9px 14px' : '10px 10px 10px 16px', marginTop: mobile ? 14 : 16 }}>
              <Sparkles size={mobile ? 15 : 17} color="var(--coral)" strokeWidth={2} />
              <input value={steerText} onChange={(e) => setSteerText(e.target.value)} placeholder={t('script.steerPlaceholder')} style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', font: `500 ${mobile ? 13 : 14}px var(--font-body)`, color: 'var(--ink)' }} />
              <button type="submit" className="q-btn press" disabled={busy || !steerText.trim()} style={{ background: 'var(--fill-ghost)', color: 'var(--ink)', padding: mobile ? '8px 13px' : '10px 17px', borderRadius: mobile ? 10 : 12, font: `700 ${mobile ? 11.5 : 12.5}px var(--font-body)`, opacity: (busy || !steerText.trim()) ? 0.5 : 1 }}>{t('script.send')}</button>
            </form>
          )}

          {/* steer confirm gate (30 credits) */}
          {scriptGate && !compiling && (
            <div className="q-card" style={{ marginTop: 12, padding: 14, borderRadius: 14, border: '1px solid var(--coral-line)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ flex: 1, font: '500 13px var(--font-body)', color: 'var(--text-muted)' }}>{scriptGate.ooc ? t('script.needsBalance', { required: scriptGate.credits_required, balance: scriptGate.credits_balance }) : t('script.steerPrice', { credits: (scriptGate.quote && scriptGate.quote.credits && scriptGate.quote.credits.credits) || '—' })}</span>
              {scriptGate.ooc ? (
                <button className="q-btn press" onClick={openTopUp} style={{ background: 'var(--coral)', color: '#fff', padding: '10px 18px', borderRadius: 12, font: '700 13px var(--font-body)' }}>{t('script.outOfCredits')}</button>
              ) : (
                <>
                  <button className="q-btn press btn-ghost" onClick={() => setScriptGate(null)}>{t('common.cancel')}</button>
                  <button className="q-btn press" disabled={busy} onClick={() => runScript({ steer: scriptGate.steer, confirm: true })} style={{ background: 'var(--coral)', color: '#fff', padding: '10px 18px', borderRadius: 12, font: '700 13px var(--font-body)', opacity: busy ? 0.6 : 1 }}>{t('ideas.confirmSpend')}</button>
                </>
              )}
            </div>
          )}

          {/* the compile beat (Approve triggered the free COMPILE job) */}
          {compiling && (
            <div style={{ textAlign: 'center', padding: '28px 0 8px' }}>
              <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 999, background: 'var(--violet)', animation: 'pulseDot 1.3s ease-in-out infinite' }} />
              <p className="muted" style={{ marginTop: 14, font: '600 14px var(--font-body)' }}>{t('script.compiling')}</p>
            </div>
          )}

          {/* APPROVE step 1 — the hard gate: read + approve → compile (no credits yet) */}
          {!compiling && !renderQuote && !renderGate && !scriptGate && (
            <button className="q-btn press btn-primary" disabled={busy} onClick={runCompile} style={{ marginTop: 16, width: mobile ? '100%' : 'auto', minWidth: mobile ? undefined : 220 }}>{t('script.approveScript')} →</button>
          )}

          {/* APPROVE step 2/3 — the render quote (after compile) + confirm */}
          {(renderQuote || ooc) && !compiling && (mobile ? (
            <>
              <button className="q-btn press btn-primary" disabled={ooc ? false : (!renderQuote || busy)} onClick={ooc ? openTopUp : confirmRender} style={{ marginTop: 12 }}>{ooc ? t('script.outOfCredits') : <>{t('script.approve')} →</>}</button>
              <div style={{ textAlign: 'center', font: '500 11px var(--font-mono)', color: ooc ? 'var(--coral)' : 'var(--text-faint)', marginTop: 9 }}>
                {ooc
                  ? t('script.needsBalance', { required: renderGate.credits_required, balance: renderGate.credits_balance })
                  : seconds == null ? t('common.loading')
                    : credited ? t('script.priceLine', { seconds, time: t('script.renderTime'), credits: creditFace.credits })
                      : t('script.priceFree', { seconds, time: t('script.renderTime') })}
              </div>
            </>
          ) : (
            <div className="q-card" style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 16, padding: '13px 16px', boxShadow: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, font: '500 12.5px var(--font-body)', color: 'var(--text-muted)' }}>
                {seconds != null ? (
                  <>
                    <span>{seconds}s</span><span style={{ color: 'var(--border-strong)' }}>·</span>
                    <span>{t('script.renderTime')}</span><span style={{ color: 'var(--border-strong)' }}>·</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {credited && <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--tangerine)' }} />}
                      {credited ? t('script.credits', { credits: creditFace.credits }) : t('script.free')}
                    </span>
                  </>
                ) : <span>{t('common.loading')}</span>}
              </div>
              {ooc ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="muted" style={{ fontSize: 12 }}>{t('script.needsBalance', { required: renderGate.credits_required, balance: renderGate.credits_balance })}</span>
                  <button className="q-btn press" onClick={openTopUp} style={{ background: 'var(--coral)', color: '#fff', padding: '13px 22px', borderRadius: 12, font: '700 14px var(--font-body)', boxShadow: '0 6px 16px rgba(255,81,71,.28)' }}>{t('script.outOfCredits')}</button>
                </div>
              ) : (
                <button className="q-btn press" disabled={!renderQuote || busy} onClick={confirmRender} style={{ background: 'var(--coral)', color: '#fff', padding: '13px 24px', borderRadius: 12, font: '700 14px var(--font-body)', boxShadow: '0 6px 16px rgba(255,81,71,.28)', opacity: (!renderQuote || busy) ? 0.6 : 1 }}>{t('script.approve')} →</button>
              )}
            </div>
          ))}
          {creditFace && creditFace.credits_hold > creditFace.credits && !ooc && (
            <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>{t('script.holdNote', { hold: creditFace.credits_hold })}</p>
          )}
          {renderGate && renderGate.kind === 'refused' && <p className="muted" style={{ color: 'var(--coral)' }}>{renderGate.error}</p>}
        </>
      )}

      {err && draft && <p className="muted" style={{ color: 'var(--coral)', marginTop: 12 }}>{err}</p>}
    </div>
  );
}

// the readable scene-card list (P2-SPLIT): one card per clip — what you SEE / what's SAID / on
// screen / how it FEELS. `partial` cards (still streaming) show just the slug coming in.
function SceneList({ scenes, mobile, t, partial = false }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: mobile ? 10 : 12 }}>
      {scenes.map((s, i) => {
        const acc = SCENE_ACCENTS[i % 4];
        return (
          <div key={s.id || i} className="scene-row qcard" style={mobile ? { borderRadius: 15, padding: 13, gap: 12 } : undefined}>
            <div className="scene-num" style={{ width: mobile ? 34 : 44, height: mobile ? 34 : 44, borderRadius: mobile ? 10 : 12, background: `var(--${acc}-tint)`, color: `var(--${acc})`, fontSize: mobile ? 13 : 15 }}>{s.index || i + 1}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {s.slug && <div style={{ font: `700 ${mobile ? 12.5 : 14}px var(--font-body)`, color: 'var(--ink)', marginBottom: 3 }}>{s.slug}{s.seconds ? <span className="muted" style={{ font: `500 ${mobile ? 10 : 11}px var(--font-mono)`, marginLeft: 8 }}>{s.seconds}s</span> : null}</div>}
              {s.visual && <div className="mono" style={{ font: `500 ${mobile ? 11 : 12.5}px/1.5 var(--font-body)`, color: 'var(--text-muted)', marginBottom: (s.line || s.text) ? 7 : 0 }}>{s.visual}</div>}
              {s.line && <div style={{ font: `500 ${mobile ? 14 : 16}px/1.4 var(--font-body)`, color: 'var(--ink)' }}>“{s.line}”</div>}
              {s.text && <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 7, padding: '3px 9px', borderRadius: 7, background: 'var(--fill-ghost)', font: `600 ${mobile ? 10 : 11}px var(--font-mono)`, color: 'var(--text-faint)', letterSpacing: '.02em' }}><span>{t('script.onscreen').toUpperCase()}</span><span style={{ color: 'var(--ink)' }}>{s.text}</span></div>}
              {s.emotion && !partial && <div style={{ font: `italic 500 ${mobile ? 11.5 : 12.5}px var(--font-body)`, color: 'var(--text-faint)', marginTop: 6 }}>{s.emotion}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
